import Box from "@mui/material/Box";
import AddIcon from "@mui/icons-material/Add";
import { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { Layout, Paper, Button } from "$components";
import useJStyles from "$hooks/useJStyles";
import {
  DataTableWithContext,
  EnumBadgeType,
  EnumDataRowType,
  EnumFilterTypes,
} from "$components/DataTable";
import { ImportButtonWithModal } from "$components/Forms/ImportButtonWithModal";
import { Switch } from "$components/Forms/Switch";
import { appState } from "$reducers";
import { usePopin } from "$components/Popin/usePopin";
import useAuth from "$hooks/useAuth";
import { useQueryParameters } from "$hooks/useQueryParameters";
import { getProjects } from "$services/Projects.service";
import { ImportType } from "$types/import/ImportType";
import { getProjectStatusOptions } from "$utils/typeConstants/Project";
import { ProjectPopinForm } from "./ProjectPopinForm";
import { CacheInvalidationAction } from "$utils/cache";

export interface IprojectForm {
  togglePopin: any;
}

const useMakeCell = (value, type, badge?) => {
  const history = useHistory();
  const link = ({ _id }) => `/projets/${_id}`;

  return {
    type,
    link,
    value,
    badge,
    onClick: (row) => {
      history.push(link(row));
    },
  };
};

const Projects = () => {
  const [isActive, setisActive] = useState(true);
  const { jclasses } = useJStyles();
  const { Popin, PopinProps, togglePopin } = usePopin();
  const params = useQueryParameters();
  const { filter: companySidebarFilter } = useSelector(appState);
  const { isAdmin } = useAuth();

  const headerData = [
    {
      id: "companyName",
      filter: {
        type: EnumFilterTypes.INPUT,
        label: "Entreprise",
        placeholder: "Par nom",
        value: (companySidebarFilter?.name || params.get("companyName")) ?? "",
        disabled: !!companySidebarFilter?.name,
      },
      minWidth: 170,
      cell: {
        ...useMakeCell(({ company }) => company.name, EnumDataRowType.Link),
        isDisabled: !isActive,
      },
    },
    {
      id: "nameCode",
      filter: {
        type: EnumFilterTypes.INPUT,
        label: "Projet",
        placeholder: "Par nom ou code",
        value: params.get("nameCode"),
      },
      minWidth: 170,
      cell: {
        ...useMakeCell(({ code, name }) => {
          return { title: code, subtitle: name, isLarge: true };
        }, EnumDataRowType.Link),
        isDisabled: !isActive,
      },
    },
    {
      id: "status",
      filter: {
        type: EnumFilterTypes.SELECT,
        value: params.get("status"),
        styleFilter: { maxWidth: 250 },
        options: getProjectStatusOptions(),
        label: "statut",
      },
      minWidth: 170,
      cell: {
        ...useMakeCell(
          ({ status }) => status,
          EnumDataRowType.Badge,
          EnumBadgeType.Project
        ),
        isDisabled: !isActive,
      },
    },
  ];

  const handleSwitch = () => {
    setisActive(!isActive);
  };

  const externalState = useMemo(() => {
    const returnState = {};
    // eslint-disable-next-line no-restricted-syntax
    for (const [key, value] of params.entries()) {
      returnState[key] = value;
    }

    const { page, rowsPerPage, ...filters } = returnState as any;

    return {
      filters: {
        ...filters,
        isActive,
        ...(isAdmin() &&
          companySidebarFilter?.name && {
            companyId: companySidebarFilter?._id,
          }),
      },
      pagination: { page, rowsPerPage },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, params, companySidebarFilter]);

  return (
    <Box className={jclasses.jWrapper}>
      <Layout
        title="Liste des projets"
        contentClassName={jclasses.jLayoutCustom}
        isHeaderRight
      >
        <Paper className={jclasses.jPaper}>
          <DataTableWithContext
            externalState={externalState}
            filterList={[
              <Switch
                label="Projets actifs"
                onChange={handleSwitch}
                checked={isActive}
              />,
            ]}
            buttonList={[
              <Button
                variant="outlined"
                onClick={togglePopin}
                startIcon={<AddIcon />}
                disabled={!isAdmin()}
              >
                Ajouter un projet
              </Button>,
              <ImportButtonWithModal
                title="Opérations en masse"
                dataName="projets"
                disabled={!isAdmin()}
                importType={ImportType.PROJECT}
                additionalFilters={{
                  companyId: companySidebarFilter?._id,
                }}
                buttonTexts={{
                  importButtonText: "Importer des projets",
                  updateButtonText: "Mettre à jour des projets",
                }}
              />,
            ]}
            rowsPerPage={50}
            thead={headerData}
            queryFunction={getProjects}
            queryFunctionName={CacheInvalidationAction.GET_PROJECTS}
            data-testid="projectList"
          />
        </Paper>
        <Popin {...PopinProps}>
          <ProjectPopinForm togglePopin={togglePopin} />
        </Popin>
      </Layout>
    </Box>
  );
};
export default Projects;
